import { apiClient } from '../utils/api-client';
import { ApiCodedError, throwForResponse } from './api-error';
import type { EntryField, EntryOptions } from '../types/trophy';

/**
 * One entry of a squad at one competition, as its team manager sees it.
 *
 * Every row of the squad is listed, however it was made — by the desk, by the
 * athlete, or by a team manager — but only the rows a team manager made carry
 * their details and can be changed from here (`team_entry`, `editable`). The
 * rest belong to whoever wrote them.
 */
export interface TeamEntry {
  id: string;
  name?: string | null;
  surname?: string | null;
  full_name: string;
  team_id?: string | null;
  team_name?: string | null;
  /** Null until the desk sorts them into a class. */
  category_id?: string | null;
  category_name?: string | null;
  /**
   * The turn booked, when the class is split over several bookable rounds;
   * null is every one of them — or, with `turn_to_choose`, one still to book.
   */
  competition_round_id?: string | null;
  competition_round_name?: string | null;
  turn_to_choose?: boolean;
  /** Confirmed by the desk: climbing, and scorable. */
  confirmed: boolean;
  /** Made by a team manager. */
  team_entry: boolean;
  /**
   * Picked from the squad's athletes at another stage rather than typed in:
   * the date of birth and the address came from that entry, were never shown
   * to the squad, and are not sent here either — only the names and the turn
   * can be corrected.
   */
  reentered?: boolean;
  /** The squad may change or withdraw it right now. */
  editable: boolean;
  /**
   * The fields below are sent only on a team entry: they are what the squad
   * typed. On a re-entered one, only the gender and the class are.
   */
  birth_date?: string | null;
  minor?: boolean;
  /**
   * The gender of the class they compete in. Null on a row made before the
   * squad was asked it, or where the organiser's agreement does not cover it.
   */
  gender?: 'male' | 'female' | null;
  /**
   * The desk chose this class by hand: it stays whatever date or gender is
   * typed, so the form shows it rather than working one out.
   */
  category_chosen?: boolean;
  /** The athlete's own address, from 14. */
  email?: string | null;
  /** The parent's or guardian's address, under 14. */
  guardian_email?: string | null;
  /** When the letter about this entry went out, if one has. */
  invited_at?: string | null;
}

/**
 * Why the squad cannot write right now. `on_hold` and `dpa_required` are the
 * same cause — the organiser's data processing agreement is not on file — told
 * as such only to whoever works the desk.
 */
export type TeamEntriesBlock = 'entries_closed' | 'on_hold' | 'dpa_required';

export interface TeamEntriesPage {
  entries: TeamEntry[];
  /** The squads this person may enter people for here. */
  teams: { id: string; name: string }[];
  open: boolean;
  blockedReason: TeamEntriesBlock | null;
  /**
   * Whether a squad's row may hold the gender here — the organiser signed the
   * agreement that names it. The form asks it only then.
   */
  genderCollected: boolean;
  /**
   * Whether the squad's athletes from the circuit's other stages can be looked
   * up and entered again here: a stage whose organiser signed the agreement
   * that covers it. "Enter an athlete" opens on that search when it is true.
   */
  reEntry: boolean;
}

/**
 * Somebody who competed with the squad at another stage of the circuit and is
 * not entered here yet, as a team manager is shown them: never an address or
 * the date of birth, only which of gender and date of birth the earlier entry
 * lacks (and the dialog will ask).
 */
export interface TeamEntryCandidate {
  /** The earlier entry's id: what entering them again names (`from_entry_id`). */
  id: string;
  name: string;
  surname: string;
  full_name: string;
  team_id: string;
  team_name: string;
  /** The stage they last climbed with the squad. */
  stage_name: string;
  /** Their class there, if they had one. */
  category_name: string | null;
  missing: EntryField[];
}

export interface TeamEntryCandidates {
  candidates: TeamEntryCandidate[];
  /** How many the search found; more than are listed when it hit the cap. */
  total: number;
}

/**
 * Entering one of those candidates again: the server builds the row from
 * their earlier entry, and takes from here only what it lacks — the gender,
 * the date of birth — and the turn.
 */
export interface TeamReEntryInput {
  from_entry_id: string;
  gender?: 'male' | 'female' | null;
  birth_date?: string | null;
  competition_round_id?: string | null;
}

/**
 * What the squad types: an address is optional, and which one depends on the
 * age; the gender where the organiser's agreement covers it; the turn when the
 * class is split over two or more. A row the squad re-entered takes only the
 * names and the turn (the rest is not the squad's to see).
 */
export interface TeamEntryInput {
  name: string;
  surname: string;
  birth_date?: string;
  email?: string | null;
  guardian_email?: string | null;
  competition_team_id?: string | null;
  gender?: 'male' | 'female' | null;
  competition_round_id?: string | null;
}

/**
 * A refused write, with what was wrong per field — keyed on the attribute the
 * server names, with the server's code so the form can say it in its own words.
 */
export class TeamEntryValidationError extends ApiCodedError {
  fields: Record<string, { code?: string; detail: string }>;

  constructor(message: string, code: string | undefined, fields: Record<string, { code?: string; detail: string }>) {
    super(message, code);
    this.name = 'TeamEntryValidationError';
    this.fields = fields;
  }
}

interface JsonApiError {
  detail?: string;
  code?: string;
  source?: { pointer?: string };
}

async function throwForWrite(response: Response, fallback: string): Promise<never> {
  if (response.status !== 422) {
    return throwForResponse(response, fallback);
  }

  const body = (await response.json().catch(() => ({}))) as { errors?: JsonApiError[] };
  const errors = body.errors ?? [];
  const fields: Record<string, { code?: string; detail: string }> = {};
  errors.forEach((error) => {
    const field = error.source?.pointer?.split('/').pop();
    if (field && !fields[field]) fields[field] = { code: error.code, detail: error.detail ?? fallback };
  });

  throw new TeamEntryValidationError(errors[0]?.detail ?? fallback, errors[0]?.code, fields);
}

class TeamEntriesService {
  /** The squad's entries here, and whether — and why not — it may write. */
  async list(competitionSlug: string): Promise<TeamEntriesPage> {
    const response = await apiClient.get(`/api/competitions/${competitionSlug}/team_entries`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to load the squad');
    }

    const body: {
      data: Array<{ attributes: TeamEntry }>;
      meta: {
        teams: { id: string; name: string }[];
        open: boolean;
        blocked_reason: TeamEntriesBlock | null;
        gender_collected?: boolean;
        re_entry?: boolean;
      };
    } = await response.json();

    return {
      entries: body.data.map((row) => row.attributes),
      teams: body.meta.teams,
      open: body.meta.open,
      blockedReason: body.meta.blocked_reason,
      genderCollected: !!body.meta.gender_collected,
      reEntry: !!body.meta.re_entry,
    };
  }

  /**
   * The squad's athletes from the circuit's other stages who are not entered
   * here yet, found by name or surname — or by an address, typed in full. A
   * POST so that address never sits in a URL.
   */
  async candidates(
    competitionSlug: string,
    { search, teamId }: { search?: string; teamId?: string | null } = {}
  ): Promise<TeamEntryCandidates> {
    const response = await apiClient.post(`/api/competitions/${competitionSlug}/team_entries/candidates`, {
      search: search ?? '',
      ...(teamId ? { team_id: teamId } : {}),
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to search the squad');
    }

    const body: { data: Array<{ attributes: TeamEntryCandidate }>; meta: { total: number } } = await response.json();
    return { candidates: body.data.map((row) => row.attributes), total: body.meta.total };
  }

  /**
   * The class and the rounds of a row whose date of birth the squad is not
   * shown — a candidate (with what it lacks, as typed), or a row the squad
   * re-entered — worked out by the server from the values it holds.
   */
  async entryOptions(
    competitionSlug: string,
    body:
      | { from_entry_id: string; gender?: string | null; birth_date?: string | null }
      | { entry_id: string }
  ): Promise<EntryOptions & { missing: EntryField[] }> {
    const response = await apiClient.post(`/api/competitions/${competitionSlug}/team_entries/entry_options`, body);

    if (!response.ok) {
      await throwForResponse(response, 'Could not read the categories');
    }

    const data: { data: EntryOptions & { missing: EntryField[] } } = await response.json();
    return data.data;
  }

  /**
   * Enters one of the squad's athletes from another stage again. The same
   * declaration as for anybody typed in.
   */
  async reEnter(competitionSlug: string, input: TeamReEntryInput, declaration: boolean): Promise<TeamEntry> {
    const response = await apiClient.post(`/api/competitions/${competitionSlug}/team_entries`, {
      data: { attributes: { ...input, declaration } },
    });

    if (!response.ok) {
      await throwForWrite(response, 'Failed to enter the athlete');
    }

    const body: { data: { attributes: TeamEntry } } = await response.json();
    return body.data.attributes;
  }

  /**
   * Enters one athlete for the squad. `declaration` is the squad saying the
   * athletes, or their parents, agreed: the server refuses without it.
   */
  async create(competitionSlug: string, input: TeamEntryInput, declaration: boolean): Promise<TeamEntry> {
    const response = await apiClient.post(`/api/competitions/${competitionSlug}/team_entries`, {
      data: { attributes: { ...input, declaration } },
    });

    if (!response.ok) {
      await throwForWrite(response, 'Failed to enter the athlete');
    }

    const body: { data: { attributes: TeamEntry } } = await response.json();
    return body.data.attributes;
  }

  async update(competitionSlug: string, id: string, input: TeamEntryInput): Promise<TeamEntry> {
    const response = await apiClient.patch(`/api/competitions/${competitionSlug}/team_entries/${id}`, {
      data: { attributes: input },
    });

    if (!response.ok) {
      await throwForWrite(response, 'Failed to save the athlete');
    }

    const body: { data: { attributes: TeamEntry } } = await response.json();
    return body.data.attributes;
  }

  async withdraw(competitionSlug: string, id: string): Promise<void> {
    const response = await apiClient.delete(`/api/competitions/${competitionSlug}/team_entries/${id}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to withdraw the athlete');
    }
  }
}

export const teamEntriesService = new TeamEntriesService();
